function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
    this.dataset.notification == "true"
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  // onButtonClick(event) {
  //   event.preventDefault();
  //   const previousValue = this.input.value;

  //   if (event.target.name === 'plus') {
  //     if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
  //       this.input.value = this.input.dataset.min;
  //     } else {
  //       this.input.stepUp();
  //     }
  //     if (this.dataset.notification) {
  //       document.querySelector(".quick-notify").classList.add("active");
  //     }
  //   } else {
  //     this.input.stepDown();
  //   }

  //   if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

  //   if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
  //     this.input.value = parseInt(this.input.min);
  //   }
  // }
  onButtonClick(event) {
  event.preventDefault();
  const previousValue = this.input.value;

  if (event.target.name === 'plus') {
    if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
      this.input.value = this.input.dataset.min;
    } else {
      this.input.stepUp();
    }

    if (this.dataset.notification) {
      const notify = document.querySelector(".quick-notify");
      const drawernotify = document.querySelector(".drawer-quick-notify");
      if (notify) {
        notify.classList.add("active");
      }
      if (drawernotify) {
        setTimeout(() => drawerNotify.classList.remove("active"), 5000);
      }
    }
  } else if (event.target.name === 'minus') {
  this.input.stepDown();
  const bubble = document.querySelector(".animate-btm-bubble");
  if (bubble) {
    const cartItemValue = parseInt(bubble.dataset.cartitem || 0);
    if (cartItemValue === 1) {
      const notify = document.querySelector(".quick-notify");
      if (notify) notify.classList.remove("active");
    }
  }
}
  if (previousValue !== this.input.value) {
    this.input.dispatchEvent(this.changeEvent);
  }

  if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
    this.input.value = parseInt(this.input.min);
  }
}


  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );
    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)'
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    // If the click was on the navigable <a> link (not the toggle circle), let navigation happen
    if (event.target.closest('a.menu-drawer__menu-link, a.submenu_link')) return;

    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      if(summaryElement.nextElementSibling, detailsElement.querySelector('button')) trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 992px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    /* `open` used to be removed on the next frame, so the panel vanished
       before the 0.35s slide-out could run — opening animated, closing did
       not. Wait for the panel's own transform transition instead, with a
       timeout so a missing transition can never leave the panel stuck open. */
    const panel =
      detailsElement.querySelector(':scope > .menu-drawer__submenu') ||
      detailsElement.querySelector(':scope > .menu-drawer');

    const finish = () => {
      if (detailsElement._closing !== true) return;
      detailsElement._closing = false;
      detailsElement.removeAttribute('open');
      if (detailsElement.closest('details[open]')) {
        trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
      }
    };

    detailsElement._closing = true;

    if (!panel) {
      window.requestAnimationFrame(finish);
      return;
    }

    const onEnd = (e) => {
      if (e.target !== panel || e.propertyName !== 'transform') return;
      panel.removeEventListener('transitionend', onEnd);
      finish();
    };
    panel.addEventListener('transitionend', onEnd);

    const ms = parseFloat(getComputedStyle(panel).transitionDuration || '0') * 1000;
    setTimeout(() => {
      panel.removeEventListener('transitionend', onEnd);
      finish();
    }, (ms || 350) + 60);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector('.section-header');
    this.borderOffset =
      this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
    );
    this.header.classList.add('menu-open');

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');
    window.removeEventListener('resize', this.onResize);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
      );
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  };
}

customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('[id^="ModalClose-"]').forEach(ele=> ele.addEventListener('click', this.hide.bind(this, false)));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      /* Was: pointerType === 'mouse' only, so touch devices could never close
         by tapping out; and it fired on the image itself, so a mouse click on
         the photo closed the lightbox. Now: close only on a tap/click that
         lands outside the media, and only if the pointer did not travel (a
         swipe or pinch must not count as an outside tap). */
      this._mdStart = null;
      this.addEventListener('pointerdown', (event) => {
        this._mdStart = { x: event.clientX, y: event.clientY };
      });
      this.addEventListener('pointerup', (event) => {
        const start = this._mdStart;
        this._mdStart = null;
        if (start && Math.hypot(event.clientX - start.x, event.clientY - start.y) > 10) return;
        if (
          event.target.closest(
            'deferred-media, product-model, model-viewer, video, iframe, img, .product-media-modal__toggle, button, a'
          )
        )
          return;
        this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    // this.dataset.section = this.closest('.shopify-section').id.replace('shopify-section-', '');
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }
  hide() {    
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    if (this.classList.contains('popup_faqs')) {
      this.classList.add("popup_closing");
      setTimeout(() => {
        this.removeAttribute('open');
        this.classList.remove("popup_closing");
      }, 500);
    }
    else {
      this.removeAttribute('open');
    }
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
  // hide() {
  //   document.body.classList.remove('overflow-hidden');
  //   document.body.dispatchEvent(new CustomEvent('modalClosed'));
  //   this.removeAttribute('open');
  //   removeTrapFocus(this.openedBy);
  //   window.pauseAllMedia();
  // }
 
}
customElements.define('modal-dialog', ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === '') {
        const productUrl = this.dataset.url.split('?')[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const sourceQty = html.querySelector('.quick-order-list-container').parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(`#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`)
    );
  }
}

customElements.define('bulk-modal', BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        deferredElement.play();
      }

      // Workaround for safari iframe bug
      const formerStyle = deferredElement.getAttribute('style');
      deferredElement.setAttribute('style', 'display: block;');
      window.setTimeout(() => {
        deferredElement.setAttribute('style', formerStyle);
      }, 0);
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    /* Opt-in looping for plain sliders (PDP media gallery). SlideshowComponent
       overrides this to true for itself. */
    this.enableSliderLooping = this.dataset.loop === 'true';
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    this.oriantation = window.matchMedia('(min-width: 768px)').matches ? this.dataset.oriantation ? this.dataset.oriantation : 'horizontal' :  'horizontal';
    if(this.oriantation == 'vertical'){
      this.offsetPosition = 'offsetTop';
      this.scrollPosition = 'scrollTop';
      this.clientPosition = 'clientHeight';
    }else{
      this.offsetPosition = 'offsetLeft';
      this.scrollPosition = 'scrollLeft';
      this.clientPosition = 'clientWidth';
    }

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
    

  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element[this.clientPosition] > 0);
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1][this.offsetPosition] - this.sliderItemsToShow[0][this.offsetPosition];
    this.slidesPerPage = Math.floor(
      (this.slider.clientHeight - this.sliderItemsToShow[0][this.offsetPosition]) / this.sliderItemOffset
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    const sliderPosition =  this.slider[this.scrollPosition];
    this.currentPage = Math.round(sliderPosition / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }
    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) return;

    if (this.isSlideVisible(this.sliderItemsToShow[0]) && sliderPosition === 0) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  isSlideVisible(element, offset = 0) {
    if (!element) return false;                                    
    const lastVisibleSlide = this.slider[this.clientPosition] + this.slider[this.scrollPosition] - offset;
    return element[this.offsetPosition] + element[this.clientPosition] <= lastVisibleSlide && element[this.offsetPosition] >= this.slider[this.scrollPosition];
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    const sliderPosition = this.slider[this.scrollPosition];
    const isNext = event.currentTarget.name === 'next';

    this.slideScrollPosition = isNext
      ? sliderPosition + step * this.sliderItemOffset
      : sliderPosition - step * this.sliderItemOffset;

    /* Wrap around the ends. Native scroll-snap clamps at the edges, so
       without this the last slide simply refused to advance. */
    if (this.enableSliderLooping) {
      const scrollSize =
        this.oriantation === 'vertical' ? this.slider.scrollHeight : this.slider.scrollWidth;
      const maxScroll = scrollSize - this.slider[this.clientPosition];

      if (isNext && sliderPosition >= maxScroll - 1) {
        this.slideScrollPosition = 0;
      } else if (!isNext && sliderPosition <= 1) {
        this.slideScrollPosition = maxScroll;
      }
    }

    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    const param = this.oriantation == 'vertical' ? {top:position} : {left:position};
    this.slider.scrollTo(param);
  }
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', () => {
        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex');
          });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1');
          });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === 'next' ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
    const direction = shouldMoveNext ? 'next' : 'previous';

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define('slideshow-component', SlideshowComponent);

// Variants

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);
      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }


  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`fieldset`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }
}
customElements.define('variant-selects', VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (!this.querySelector('slider-component') && this.classList.contains('complementary-products')) {
          this.remove();
        }

        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded');
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('product-recommendations', ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector('.icon');
  }

  connectedCallback() {
    document.addEventListener('storefront:signincompleted', this.handleStorefrontSignInCompleted.bind(this));
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define('account-icon', AccountIcon);

class BulkAdd extends HTMLElement {
  constructor() {
    super();
    this.queue = [];
    this.requestStarted = false;
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });
    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, 250);
  }

  sendRequest(queue) {
    this.requestStarted = true;
    const items = {};
    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));
    const quickBulkElement = this.closest('quick-order-list') || this.closest('quick-add-bulk');
    quickBulkElement.updateMultipleQty(items);
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      this.startQueue(index, inputValue);
    }
  }

  getSectionsUrl() {
    if (window.pageNumber) {
      return `${window.location.pathname}?page=${window.pageNumber}`;
    } else {
      if (window.location.pathname == '/search') {
        const urlParams = new URLSearchParams(window.location.search);
        const queryString = urlParams.get('q');
        return `${window.location.pathname}?q=${queryString}`;
      }
      return `${window.location.pathname}`;
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

if (!customElements.get('bulk-add')) {
  customElements.define('bulk-add', BulkAdd);
}

// login popup
class ContentDropdown extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('click', this.popUpClick.bind(this));
    document.addEventListener('click', this.closePopup.bind(this));
    document.addEventListener('keydown', this.escClose.bind(this));
  }
  popUpClick(event) {
    event.stopPropagation();
    const flag = this.querySelector("#content_dropdown").classList.contains("hidden");
    document.querySelectorAll("#content_dropdown").forEach((e) => e.classList.add("hidden"));
    if(flag) this.querySelector("#content_dropdown").classList.remove("hidden");
      else this.querySelector("#content_dropdown").classList.add("hidden");
  }
  closePopup(event) {
    const userPopup = this.querySelector("#content_dropdown");
    if (!userPopup.contains(event.target)) {
      userPopup.classList.add("hidden");
    }
  }
  escClose(e) {
    if (e.key === "Escape") {
      const userPopup = this.querySelector("#content_dropdown");
      userPopup.classList.add("hidden");
    }
  }
}
customElements.define('content-dropdown', ContentDropdown);

class DesktopWebiMenu extends HTMLElement {
  constructor() {  
    super();
    this.restrictedList = [".menudrop_hover"];
    if(window.outerWidth > 991){
      this.touch = "ontouchstart" in document.documentElement;
      const drop = this.dataset.drop && (!this.touch) ? this.dataset.drop : 'click';
      this.addEventListener('click', this.onFocusOut.bind(this));
      this.addEventListener('keydown', this.handleKeyDown.bind(this));
      this.querySelectorAll('li.menu_space').forEach(ele =>{
        ele.addEventListener('keydown', this.handleLiKeyDown.bind(this));
        if(drop == 'mouseover'){
          // ── Open submenu on hover ──
          // Use mouseenter: fires exactly once when cursor enters the <li>,
          // does NOT bubble, no relatedTarget check needed — eliminates
          // intermittent misses caused by cursor moving between child elements.
          ele.addEventListener('mouseenter', (event) => {
            this.onLiHover(ele);
            this._wm_startTracking(ele);
          });
          ele.addEventListener('mousemove', (event) => {
            if (ele.classList.contains('menuclick')) return;
            const d = ele.querySelector('.menudrop_hover');
            if (d && d.contains(event.target)) return;
            this.onLiHover(ele);
            this._wm_startTracking(ele);
          });
        }else if(drop== 'click'){
          ele.addEventListener(drop, (event)=>{
            if(this.checkRestrictedElement(event))
              this.onLiClick(ele);
          });
          this._boundOutsideClickHandler = this.handleOutsideClick.bind(this);
          document.addEventListener('click', this._boundOutsideClickHandler);
        }
      });
    }
  }
  checkRestrictedElement(event){
    let elementCheck = true;
    this.restrictedList.forEach((list) => {
      this.querySelectorAll('.menudrop_hover').forEach((e) => {
          if(e.contains(event.target)) elementCheck = false;
      });
    });
    return elementCheck;
  }
  handleOutsideClick(event) {
    if (!this.contains(event.target)) {
      this.classList.remove('open');
      this.closeLi();
    }
  }
  removeSubMenuClass(ele){
    ele.querySelectorAll('.submenu_child').forEach((e)=>{
      e.classList.remove('submenu_keyboard')
    });
  }
  addSubMenuClass(ele){
    ele.querySelectorAll('.submenu_child').forEach((e)=>{
      e.classList.add('submenu_keyboard')
    });
  }
  handleLiKeyDown(event){
    if (event.keyCode === 13) {
      if(event.target.classList.contains('menuclick')){
        event.target.classList.remove('menuclick');
        this.removeSubMenuClass(event.target);
      }
      else 
        this.onLiClick(event.target);
        if(event.target.classList.contains('onlychild_focus')){
          event.preventDefault();
        }
    }
  }
  handleKeyDown(event){
    if (event.keyCode === 13) {
      this.onFocusOut(event);
      if(event.target.classList.contains('onlychild_focus')){
      event.preventDefault();
      }
    }
  }
  onLiHover(ele){
    if(ele.nodeName != 'LI') return;
    this.closeLi();
    ele.classList.add('menuclick');
    this.addSubMenuClass(ele);
  }
  onLiClick(ele){
    if(ele.nodeName != 'LI') return;
    const isOpen = ele.classList.contains('menuclick');
    this.closeLi();
    if(!isOpen) ele.classList.add('menuclick');
    this.addSubMenuClass(ele);
  }
  closeLi(){
    this._wm_stopTracking();
    this.querySelectorAll('li.menuclick').forEach((ele) =>{
      ele.classList.remove('menuclick');
      this.removeSubMenuClass(ele);
    })
  }
  onFocusOut(event){
    event.stopPropagation();
    let elementCheck = this.checkRestrictedElement(event);
    if(!this.contains(event.target) && elementCheck){
      this.closeLi();
    }
    const parentEle = event.target.parentElement.querySelector('.header__menu-item') ? event.target.parentElement.querySelector('.header__menu-item'): event.target.closest('.header__menu-item');
    if (this.touch && parentEle.classList.contains('summary') )
      event.preventDefault();
  }
  // ── Coordinate-based mouse tracking ──
  // The mega-menu dropdown uses position:absolute relative to .header-wrapper
  // (because .mega-menu{position:static} overrides .menudetails{position:relative}),
  // so it spans the FULL page width. DOM-containment mouseout checks cannot detect
  // left/right exits because the cursor never leaves the dropdown's bounding box
  // when moving sideways. We use mousemove + getBoundingClientRect instead.
  _wm_startTracking(ele) {
    this._wm_stopTracking();
    this._wm_activeEle = ele;
    this._wm_handler = (e) => {
      const cx = e.clientX, cy = e.clientY;

      // 1. Still over the <li> nav trigger itself? Stay open.
      const liRect = ele.getBoundingClientRect();
      if (cx >= liRect.left && cx <= liRect.right &&
          cy >= liRect.top  && cy <= liRect.bottom) return;

      // 2. Still over the actual dropdown content panel? Stay open.
      //    .menudrop_hover is pointer-events:none full-width wrapper — ignore its rect.
      //    Use .mm-width-wrap (mega menu inner panel) or .menudrop_hover itself
      //    when it carries page-width directly (simple dropdown).
      const drop = ele.querySelector('.menudrop_hover');
      if (drop) {
        const panel = drop.querySelector('.mm-width-wrap') || drop;
        const r = panel.getBoundingClientRect();
        if (cx >= r.left && cx <= r.right &&
            cy >= r.top  && cy <= r.bottom) return;
      }

      // 3. Cursor truly outside both — close immediately, any direction.
      this._wm_stopTracking();
      this.closeLi();
    };
    document.addEventListener('mousemove', this._wm_handler);
  }

  _wm_stopTracking() {
    if (this._wm_handler) {
      document.removeEventListener('mousemove', this._wm_handler);
      this._wm_handler = null;
    }
    this._wm_activeEle = null;
  }

  disconnectedCallback() {
    document.removeEventListener('click', this._boundOutsideClickHandler);
    this._wm_stopTracking();
  }

}
customElements.define('desktop-webi-menu', DesktopWebiMenu); 


// Carousel
class SliderCarousel extends HTMLElement {
  constructor() {
    super();
    let optionsData = {};
    let defaults = {};
    let autoplay = false;
    if(this.dataset.sectionSetting){
      const sectionSetting = JSON.parse(this.dataset.sectionSetting);
      const space = this.dataset.gapDesktop !== undefined && this.dataset.gapDesktop !== '' ? +this.dataset.gapDesktop : +this.dataset.spacingGridHorizontal;
      const spacemobile = this.dataset.gapMobile !== undefined && this.dataset.gapMobile !== '' ? +this.dataset.gapMobile : +this.dataset.spacingGridHorizontal/2;
      optionsData = {
        slidesPerView: sectionSetting.columns_desktop ? sectionSetting.columns_desktop : 1,
        spaceBetween: space,
        loop: false,
        dragClickables: false,
        updateOnWindowResize: true,
        pagination: {
          clickable: 'true',
        },
        breakpoints: {
          0: {
            slidesPerView: sectionSetting.columns_mobile,
            spaceBetween: spacemobile,
          },
          768: {
            slidesPerView: sectionSetting.columns_desktop,
            spaceBetween: space,
          }
        }
      };
      if(sectionSetting.content_type == true){
        optionsData['breakpoints'] = {
          0: {
            spaceBetween: spacemobile,
            centeredSlides: true,
            slidesPerView: 1.1,
          },
          768: { 
            slidesPerView: 1.2,
            centeredSlides: true,
            spaceBetween: space,
          },
          992: {
            slidesPerView: 1.9,
            centeredSlides: true,
            spaceBetween: space,
          }
        };
      }
      if(sectionSetting.content_type == false){
        optionsData['breakpoints'] = {
          0: {
            spaceBetween: spacemobile,
            centeredSlides: false,
            slidesPerView: 1,
          },
          768: { 
            slidesPerView: 1,
            centeredSlides: false,
            spaceBetween: space,
          }
        };
      }
      if(sectionSetting.enable_loop == true){
        optionsData['loop'] = {
          loop: true,
          rewind: true,
        };
      }
      if(sectionSetting.transition == 'tran_slide'){
        optionsData['effect'] = 'slide';
      }
      if(sectionSetting.transition == 'tran_card'){
        optionsData['effect'] = 'cards';
      }
      if(sectionSetting.transition == 'tran_coverflow'){
        optionsData['effect'] = 'coverflow';
      }

      if(this.dataset.breakpoints){
        const breakpoints = JSON.parse(this.dataset.breakpoints)
        for (const key in breakpoints) {
          if(key ==0){
            let obj = {
              slidesPerView: breakpoints[key],
              spaceBetween: spacemobile,
            }
            optionsData['breakpoints'][key] = obj
          }else{
            let customspaceBetween = space
            let breakpointsKey = key
            if(key.indexOf('m') > -1){ 
              breakpointsKey = key.split('-')[1]
              customspaceBetween = spacemobile
            }
            let obj = {
              slidesPerView: breakpoints[key],
              spaceBetween: customspaceBetween,
            }
            optionsData['breakpoints'][breakpointsKey] = obj
          }
        }
      }
      if(sectionSetting.auto_rotate == true){
        autoplay = true;
        optionsData['autoplay'] = {
          delay: sectionSetting.change_slides_speed * 1000,
          disableOnInteraction: false,
        };
      }
      if(sectionSetting.pagination_style == 'arrows' || sectionSetting.enable_slide_arrow == true){
        optionsData['navigation'] = {
          nextEl: '.swiper-button-next',
          prevEl: '.swiper-button-prev',
        };
      }

      if(sectionSetting.pagination_style == 'dots' || sectionSetting.pagination_style == 'line' || sectionSetting.pagination_style == 'counter' || sectionSetting.pagination_style == 'numbers'){
        optionsData['pagination']['el'] = '.swiper-pagination';
      }
      if(sectionSetting.pagination_style == 'line') {
        optionsData['pagination']['type'] = 'bullets';
        optionsData['pagination']['renderBullet'] = function (index, className) {
          return '<span class="' + className + '">' + '<i></i>' + '<b></b>'  + '</span>';
        };
      }
      if(sectionSetting.pagination_style == 'counter') {
        optionsData['pagination']['type'] = 'fraction';
        optionsData['pagination']['renderBullet'] = function (index, className) {
          return '<span class="' + className + '">' + '<i></i>' + '<b></b>'  + '</span>';
        };
      }
       if(sectionSetting.pagination_style == 'numbers'){
        optionsData['pagination']['renderBullet'] = function (index, className) {
          return `<span class="${className}">${index + 1}</span>`
        };
      }
    }
    this.options = {
      ...defaults,
      ...optionsData
    };
    // new Swiper(this.querySelector('.swiper'), this.options);
    this.swiper = null;
    const go = () => {
      if (this.swiper) return this.swiper;
      SliderCarousel.io && SliderCarousel.io.unobserve(this);
      ['pointerenter', 'touchstart', 'focusin'].forEach((e) => this.removeEventListener(e, go));
      const dts = this.querySelectorAll('template.hm-dslide');
      if (dts.length) {
        dts.forEach((t) => t.parentNode.replaceChild(t.content.cloneNode(true), t));
        if (window.WishlistUI && WishlistUI.updateAllButtons) WishlistUI.updateAllButtons();
      }
      this.swiper = new Swiper(this.querySelector('.swiper'), this.options);
      return this.swiper;
    };
    this.initSwiper = go;
    if ((window.Shopify && Shopify.designMode) || !('IntersectionObserver' in window) || this.getBoundingClientRect().top < window.innerHeight + 100) {
      go();
    } else {
      ['pointerenter', 'touchstart', 'focusin'].forEach((e) => this.addEventListener(e, go, { passive: true }));
      if (!SliderCarousel.io) {
        SliderCarousel.io = new IntersectionObserver(
          (entries) => {
            entries.forEach((en) => {
              if (en.isIntersecting && en.target.initSwiper) en.target.initSwiper();
            });
          },
          { rootMargin: '600px 0px' }
        );
      }
      SliderCarousel.io.observe(this);
    }
  }
  connectedCallback() {
    // new Swiper(this.querySelector('.swiper'), this.options);
    const playPauseBtn = this.querySelector('.slideshow__autoplay');
    if (!playPauseBtn) return;
    if (playPauseBtn && playPauseBtn.classList.contains('slideshow__autoplay')) {
      playPauseBtn.addEventListener('click', () => {
        this.initSwiper();
        if (this.swiper.autoplay.running) {
          this.swiper.autoplay.stop();
          playPauseBtn.classList.add('slideshow__autoplay--paused');
        } else {
          this.swiper.autoplay.start();
          playPauseBtn.classList.remove('slideshow__autoplay--paused');
        }
      });
    }
  }
}
customElements.define('slider-carousel', SliderCarousel);


// Variant hover
class ColorSwatch extends VariantSelects {
  constructor() { 
    super();
    const fieldsets = Array.from(this.querySelectorAll('fieldset label'));
    this.querySelectorAll('fieldset label').forEach((ele)=>{
      ele.addEventListener('mouseenter', this.onVariantChangeHover.bind(this));
      ele.addEventListener('click', this.onLabelClick.bind(this));
    }) 
  }
  onLabelClick(event){
    if(event.target.dataset.href){
      window.location.href = event.target.dataset.href;
    }
  }
  updatelabelDataset(ele){
    let href = ele.dataset.href;
    let optionValuesStr = '';
    if (this.selectedOptionValues.length) {
          optionValuesStr=this.selectedOptionValues.join(',');
    }
    if(href.indexOf('?option_values=') > -1){
      href = (href.substring(0, href.indexOf('?option_values=')+15) + optionValuesStr)
    }else{
      href += ("?option_values="+ optionValuesStr)
    }
    ele.dataset.href = href; 
  }
  onVariantChangeHover(event){
    // this.updatelabelDataset(event.target);
    this.querySelector('#'+event.target.getAttribute('for')).click();
    this.updatelabelDataset(event.target);
  }
}
customElements.define('color-swatch', ColorSwatch);


// Collapsible content
class CollapseContent extends HTMLElement {
  constructor() {
    super();
    this.justFocused = false;
    var collapse = this.getElementsByClassName("toggle");
    Array.from(collapse).forEach((ele) => {
      ele.setAttribute('tabindex', '0');
      var content_data = ele.nextElementSibling;
      var defaultOpen = ele.classList.contains("active");
      if (content_data && !defaultOpen) {
        content_data.style.height = '0px';
        content_data.setAttribute('data-collapsed', 'true');
        this.setInnerTabIndex(content_data, -1);
      } else {
        this.setInnerTabIndex(content_data, 0);
      }
      ele.addEventListener("click", this.onCollapseClick.bind(this));
      ele.addEventListener('keydown', this.handleKeyDown.bind(this));
      ele.addEventListener("focus", this.onFocusOpen.bind(this));
    });
  }
  handleKeyDown(event){
    if (event.keyCode === 13) {
      this.onCollapseClick(event);
    }
  }
  onFocusOpen(event) {
    const target = event.currentTarget;
    const content_data = target.nextElementSibling;
    const isCollapsed = content_data?.getAttribute('data-collapsed') === 'true';
    if (content_data && isCollapsed) {
      this.justFocused = true;
      this.closeOthers(target);
      target.classList.add("active");
      this.expandCollapse(content_data);
    }
  }
  onCollapseClick(event) {
    if (this.justFocused && event.type === 'click') {
      this.justFocused = false;
      return;
    }
    this.justFocused = false;
    event.currentTarget.classList.toggle("active");
    var content_data = event.currentTarget.nextElementSibling;
    if (!content_data) return;
    var isCollapsed = content_data.getAttribute('data-collapsed') === 'true';
    if (isCollapsed) {
      this.closeOthers(event.currentTarget);
      this.expandCollapse(content_data);
      content_data.setAttribute('data-collapsed', 'false');
    } else {
      this.allCollapse(content_data);
    }
  }
  closeOthers(current) {
    if (this.dataset.oneOpen !== 'true') return;
    Array.from(this.getElementsByClassName('toggle')).forEach((t) => {
      if (t === current) return;
      var c = t.nextElementSibling;
      if (!c || c.getAttribute('data-collapsed') === 'true') return;
      t.classList.remove('active');
      this.allCollapse(c);
    });
  }
  expandCollapse(element) {
    var collapseHeight = element.scrollHeight;
    element.style.height = collapseHeight + 'px';
    element.style.visibility = 'visible';
    this.setInnerTabIndex(element, 0);
    element.addEventListener('transitionend', () => {
      element.removeEventListener('transitionend', this.expandCollapse);
      element.style.height = null;
      element.style.visibility = 'visible';
    });
    element.setAttribute('data-collapsed', 'false');
  }
  allCollapse(element) {
    var collapseHeight = element.scrollHeight;
    element.style.height = collapseHeight + 'px';
    element.offsetHeight;
  
    element.style.transition = 'height 0.3s ease';
    element.style.height = '0px';
    this.setInnerTabIndex(element, -1);
    element.addEventListener('transitionend', () => {
      element.removeEventListener('transitionend', this.allCollapse);
      element.style.transition = '';
      element.style.height = '0px';
      element.style.visibility = 'hidden';
    });
    element.setAttribute('data-collapsed', 'true');
  }
  setInnerTabIndex(container, value) {
    if (!container) return;
    const focusable = container.querySelectorAll('a, button, input, textarea, select, [tabindex]');
    focusable.forEach(el => {
      if (value === -1 || (value === 0 && el.hasAttribute('tabindex'))) {
        el.setAttribute('tabindex', value);
      }
    });
  }
}
customElements.define('collapse-content', CollapseContent);


// Store selector
class StoreSelector extends HTMLElement {
  constructor() {
    super();
     
    let modelId = this.querySelector('modal-opener').dataset.modal;
    if(modelId) {
      this.modelEle = document.querySelector(modelId);
      if(this.modelEle) {
        this.saveBtn = this.modelEle.querySelector('#ModalClose-save');
        var value = localStorage.getItem("selected-store");
        if(value != null && value != undefined){
          const localStorageEle = document.querySelector('input[type="radio"][name="store"][value="'+value+'"]');
          if(localStorageEle){
            this.defaultStore = localStorageEle;
            this.defaultStore.checked = true;
          }else{
            localStorage.removeItem("selected-store");
            this.defaultStore = this.modelEle.querySelector('input[type=radio]:checked');
            this.setLocalStore(this.defaultStore?.value)
          }
        }else{
          this.defaultStore = this.modelEle.querySelector('input[type=radio]:checked');
          this.setLocalStore(this.defaultStore?.value)
        }
        this.changeImage(this.defaultStore);
        this.storeName  = this.querySelector(".get_storename");
        if(this.storeName) this.storeName.innerHTML = this.defaultStore.value;
      }
    }
  }
  
  setLocalStore(value){
    if(value != null && value != undefined) localStorage.setItem('selected-store', value);
  }

  connectedCallback() {
    this.saveBtn?.addEventListener('click', (event) => {
      var currentValue = this.modelEle.querySelector('input[type=radio]:checked');
      if(currentValue && currentValue != this.defaultStore){
        this.defaultStore = currentValue;
        if(this.storeName) this.storeName.innerHTML = this.defaultStore.value;
        this.setLocalStore(this.defaultStore.value);
        publish(PUB_SUB_EVENTS.customStoreSelectorChange, {
          data: { store_name: currentValue.value },
        });
      };
    });
    this.modelEle.querySelectorAll("input[type=radio]").forEach(ele => ele.addEventListener("click", this.onRadioBtnClick.bind(this)));
  }

  onRadioBtnClick(event){
    this.changeImage(event.target);
  }

  changeImage(element){
    if (!element) return;
    this.modelEle.querySelectorAll(".pickup_img").forEach(ele => ele.classList.remove('active'));
    const imgEle = this.modelEle.querySelector('.'+ element.id);
    if(imgEle){
      imgEle.classList.add('active');
    }
  }
}
customElements.define('store-selector', StoreSelector);

class LocationLabel extends HTMLElement {
  constructor() {
    super();
    this.buttons = this.querySelectorAll('.location_inner');
    this.tabs = this.querySelectorAll('.location_tab');
    this.radios = this.querySelectorAll('input[type="radio"]');
    this.prevTag = '';

    this.buttons.forEach(btn => {
      btn.addEventListener('click', this.onButtonActivate.bind(this));
      btn.addEventListener('keydown', this.onKeyControl.bind(this));
    });

    this.radios.forEach(radio => {
      radio.addEventListener('focus', () => radio.checked = true);
      radio.addEventListener('keydown', e => this.onRadioKey(e, radio));
    });
  }

  onButtonActivate(event) {
    const btn = event.currentTarget;
    const label = btn.dataset.label;

    // const radio = btn.closest('.accordion_loc').querySelector('input[type="radio"]');
    // if (radio) radio.checked = true;

    this.toggleTab(label, btn);
  }

  toggleTab(label, btn) {
    this.tabs.forEach(tab => {
      const relatedBtn = this.querySelector(`button[data-label="${tab.dataset.contant}"]`);

      if (tab.dataset.contant === label) {
        const isSame = this.prevTag === label;
        this.prevTag = isSame ? '' : label;

        tab.classList.toggle('active', !isSame);
        btn.classList.toggle('active', !isSame);
      } else {
        tab.classList.remove('active');
        relatedBtn.classList.remove('active');
      }
    });
  }

  onKeyControl(event) {
    const btn = event.currentTarget;

    switch (event.key) {
      case 'Enter':
      case ' ':
        event.preventDefault();
        this.onButtonActivate(event);
        break;

      case 'ArrowUp':
      case 'ArrowLeft':
        event.preventDefault();
        this.focusSibling(btn, -1);
        break;

      case 'ArrowDown':
      case 'ArrowRight':
        event.preventDefault();
        this.focusSibling(btn, 1);
        break;
    }
  }

  focusSibling(btn, direction) {
    const items = Array.from(this.buttons);
    let idx = items.indexOf(btn);

    let newIndex = idx + direction;
    if (newIndex < 0) newIndex = items.length - 1;
    if (newIndex >= items.length) newIndex = 0;

    items[newIndex].focus();
  }

  onRadioKey(e, radio) {
    const radios = Array.from(this.radios);
    let idx = radios.indexOf(radio);

    if (e.key === "ArrowDown" || e.key === "ArrowRight") {
      e.preventDefault();
      radios[idx + 1]?.focus();
    }
    if (e.key === "ArrowUp" || e.key === "ArrowLeft") {
      e.preventDefault();
      radios[idx - 1]?.focus();
    }
  }
}

customElements.define('location-label', LocationLabel);

// Lookbook Active

class LookBook extends HTMLElement {
  constructor() {
    super();

    this.blockId = this.dataset.blockId;
    this.buttons = this.querySelectorAll('.wblookbtn');
    this.products = this.querySelectorAll('[data-blockid]');
    this.titleHolder = this.querySelector('.main-product .ptitle-wrapper');
    this.imageWrapper = this.querySelector('.pimage-wrapper');

    this.buildProducts();
    this.setDefaultActive();
    this.bindEvents();
  }

  buildProducts() {
    if (!this.imageWrapper || !this.titleHolder) return;

    this.imageWrapper.innerHTML = '';
    this.titleHolder.innerHTML = '';

    const lookbooks = this.querySelectorAll('.wblookbook');

    lookbooks.forEach((item, index) => {
      const jsonEl = item.querySelector('[data-lookbool-product-ajax]');
      if (!jsonEl) return;

      const data = JSON.parse(jsonEl.textContent);
      const active = index === 0 ? 'active' : '';

      this.titleHolder.insertAdjacentHTML(
        'beforeend',
        `<h3 class="lookbook-block-title product-title ${active}" data-blockid="${data.id}"><a class="full-unstyled-link" href="${data.link}">${data.title}</a></h3>`
      );

      this.imageWrapper.insertAdjacentHTML(
        'beforeend',
        `<div class="product-image grid__item ${active}" data-blockid="${data.id}">
          <img src="${data.image}" alt="${data.title}">
        </div>`
      );
    });
  }

  setDefaultActive() {
    if (!this.buttons.length) return;

    this.buttons.forEach(btn => btn.classList.remove('active'));

    const firstBtn = this.buttons[0];
    firstBtn.classList.add('active');

    const blockId = firstBtn.dataset.blockid;
    if (blockId) {
      this.updateActiveStates(blockId);
    }
  }

  bindEvents() {
    this.addEventListener('click', (e) => {
      const target = e.target.closest('[data-blockid]');
      if (!target) return;
      this.updateActiveStates(target.dataset.blockid);
    });
  }

  updateActiveStates(blockId) {
    this.querySelectorAll('[data-blockid]').forEach((el) => {
      el.classList.toggle('active', el.dataset.blockid === blockId);
    });
  }
}
customElements.define('look-book', LookBook);

// Load more
class LoadMore extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('click', this.loadMoreProducts.bind(this));
    this.next_url = document.getElementById('product-grid').dataset.nextUrl;
    this.loadMoreBtn = this.querySelector('.button');
  }
  async getNextPage() {
    try {
      let res = await fetch(this.next_url);
      return await res.text();
    } catch (error) {
      console.log(error);
    } 
  }
  async loadMoreProducts() {
    const load_more_spinner = this.getElementsByClassName('load-more_spinner')[0];
    if (this.loadMoreBtn) this.loadMoreBtn.style.display = 'none';
    load_more_spinner.style.display = 'block';
    let nextPage = await this.getNextPage();
    const parser = new DOMParser();
    const nextPageDoc = parser.parseFromString(nextPage, 'text/html');
    load_more_spinner.style.display = 'none';
    const productgrid = nextPageDoc.getElementById('product-grid');
    const new_products = productgrid.getElementsByClassName('grid__item');
    const temp = new_products;
    const new_url = productgrid.dataset.nextUrl;
    if (new_url) {
      if (this.loadMoreBtn) this.loadMoreBtn.style.display = 'inline-flex';
    }
    this.next_url = new_url;
    let currentIndex = 0;
    while (new_products.length > currentIndex) {
      let product = new_products[currentIndex];
      if(product.classList.contains('collection_media')) {
        currentIndex++;
        continue;
      };
      document.getElementById('product-grid').appendChild(product);
      document.querySelector('.loadmore_count').innerHTML = document.querySelectorAll('.load_count').length;
    } 
  }
}
customElements.define('load-more', LoadMore);


// Hightlight stats
class HightlightStats extends HTMLElement {
  constructor() {
    super();
    this.section_counter = this.querySelector('#hight_stat');
    this.counters = this.querySelectorAll('.stats_count');
    this.CounterObserver = new IntersectionObserver(
      (entries) => {
        if (!entries.some(v => v.isIntersecting)){
          this.counters.forEach((counter, index) => {
            const counterNumber = +counter.innerText;
            if(counterNumber == 0) return;
            counter.innerText = "0";
          });
          return;
        };
        let speed = 100;
        this.counters.forEach((counter, index) => {
          function UpdateCounter() {
            const targetNumber = +counter.dataset.target; 
            const initialNumber = +counter.innerText;
            const incPerCount = targetNumber / speed;
            if (initialNumber < targetNumber) {
              counter.innerText = Math.ceil(initialNumber + incPerCount);
              setTimeout(UpdateCounter, 15);
            }
            else {
              counter.innerText = targetNumber;
            }
          }
          UpdateCounter();
          if (counter.parentElement.style.animation) {
            counter.parentElement.style.animation = '';
          } else {
            counter.parentElement.style.animation = `slide-up 0.3s ease forwards ${
              index / this.counters.length + 0.5
            }s`;
          }
        });
      },
      {
        root: null,
        threshold: window.innerWidth > 768 ? 0.4 : 0.3,
      }
    );
    this.CounterObserver.observe(this.section_counter);
  }
}
customElements.define('hightlight-stats', HightlightStats);


// TIMER
setInterval(countDown, 1000);
function countDown() {
  const countCardSections = document.querySelectorAll('.countd_all');
  countCardSections.forEach((countdownSection) => {
    var countDownDate = new Date(countdownSection.getAttribute('data-date')).getTime();
    var now = new Date().getTime();
    var distance = countDownDate - now;
    if (distance < 0 || isNaN(countDownDate)) {
      countdownSection.querySelector('.main_cdays .wb_cdays').textContent = "00";
      countdownSection.querySelector('.main_chours .wb_chours').textContent = "00";
      countdownSection.querySelector('.main_cminutes .wb_cminutes').textContent = "00";
      countdownSection.querySelector('.main_cseconds .wb_cseconds').textContent = "00";
    }
    else {
      countdownSection.querySelector('.main_cdays .wb_cdays').textContent = Math.floor(distance / (1000 * 60 * 60 * 24));
      countdownSection.querySelector('.main_chours .wb_chours').textContent = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      countdownSection.querySelector('.main_cminutes .wb_cminutes').textContent = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
      countdownSection.querySelector('.main_cseconds .wb_cseconds').textContent = Math.floor((distance % (1000 * 60)) / 1000);
    }
  });
}

// Quick view

class WbQuickView extends HTMLElement {
  constructor() { 
    super();
    this.addEventListener('click', this.wbPopUp.bind(this));
  }
  wbPopUp(e) {
    const button = e.target.closest('button');
    if (!button) return;
    this._openerButton = button;
    button.classList.add('loading');
    const spinner = button.querySelector('.loading__spinner');
    if (spinner) {
      spinner.classList.remove('hidden');
    }
    button.setAttribute('disabled', 'true');
    fetch(button.dataset.url?.split('?')[0] + '?view=' + button.dataset.template + 
          (button.dataset.variantid ? '&variant=' + button.dataset.variantid : ''))
      .then(response => response.text())
      .then(data => {
        if (!data) return;
        const parser = new DOMParser();
        const htmlDoc = parser.parseFromString(data, 'text/html');
        const html = htmlDoc.querySelector('[data-html="content"]').innerHTML;
        const modalSelector = document.querySelector(".js-quick_view");
        document.body.classList.add('overflow-hidden', 'modal_view');
        if (modalSelector) modalSelector.innerHTML = html;
        const modalEl = modalSelector?.querySelector('.quick-add-modal__content-info') || modalSelector?.querySelector('.cart_variant_popup .common_modal');
        if (modalEl) {
          trapFocus(modalEl);
          modalEl.focus();
          const observer = new MutationObserver(() => {
            if (!modalSelector.hasAttribute('open') && document.body.classList.contains('modal_view') === false) {
              if (this._openerButton) this._openerButton.focus();
              observer.disconnect();
            }
          });
          observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
        }

        if (window.ProductModel) window.ProductModel.loadShopifyXR();

        button.classList.remove('loading');
        if (spinner) spinner.classList.add('hidden');
        button.removeAttribute('disabled');
      })
      .catch(error => console.error('Error:', error));
  }
}
customElements.define('wb-quick-view', WbQuickView);


// Modal
class WbModal extends HTMLElement {
  constructor() { 
      super();
      this.closeEl = this.querySelector(".modal__close");
  }

  connectedCallback() {
    const _ = this;
    this.closeEl.addEventListener('click', this.close.bind(this));
    
    document.addEventListener('keydown', function(event) {
      if (event.key === 'Escape' || event.keyCode === 27) {
        _.close();
      }
    });
    
    document.addEventListener('click', function(event) {

      if (!event.target.closest('.common_modal') && !event.target.closest('modal-dialog.product-popup-modal')) {
         _.close();
      }
    });
  }
  close() {
    const _ = this;
    this.classList.add("close_popup");
    setTimeout(function() {
      _.remove();
      document.body.classList.remove('overflow-hidden', 'modal_view');
    }, 80);
  }
}
customElements.define('wb-modal', WbModal);


// Search tab
class TabModal extends HTMLElement {
  constructor() {
    super();
    this.tabs = null;
    this.tabButtons = null;
    this.contents = null;
  }

  connectedCallback() {
    this.initTabs();
  }

  initTabs() {
    this.tabs = this.querySelector(".tabs");
    this.tabButtons = this.querySelectorAll(".tab-button");
    this.contents = this.querySelectorAll(".tab-pane");

    if (!this.tabs) return;

    this.tabs.onclick = (e) => this.onTabClick(e);
    this.tabs.addEventListener("keydown", (e) => {
      if (e.keyCode === 13) this.onTabClick(e);
    });
  }

  onTabClick(e) {
    const id = e.target.dataset.id;
    if (!id) return;

    this.tabButtons.forEach((btn) => btn.classList.remove("active"));
    e.target.classList.add("active");

    this.contents.forEach((content) => content.classList.remove("active"));
    const targetElement = this.querySelector(`#${id}`);
    if (targetElement) {
      targetElement.classList.add("active");
    }
  }
}

customElements.define("tab-modal", TabModal);


/* Search move */
class SearchMove extends HTMLElement {
  connectedCallback() {
    const listElement = this.querySelector("ul");
    if (!listElement) return;

    const listItems = listElement.querySelectorAll("li");
    const itemHeight = 27; // must match CSS height
    const numberOfItems = listItems.length;

    this.style.display = "inline-block";
    this.style.overflow = "hidden";
    this.style.height = itemHeight + "px";

    if (numberOfItems <= 1) {
      listElement.style.animation = "none";
      return;
    }

    listElement.innerHTML += listElement.innerHTML;

    const scrollDistance = itemHeight * numberOfItems;

    const scrollSpeedPixelsPerSecond = 60;
    const minimumDurationSeconds = 5;
    const maximumDurationSeconds = 20;

    let calculatedDuration = scrollDistance / scrollSpeedPixelsPerSecond;

    const animationDurationSeconds = Math.min(
      Math.max(calculatedDuration, minimumDurationSeconds),
      maximumDurationSeconds
    );

    const styleSheet = document.styleSheets[0];

    for (let i = styleSheet.cssRules.length - 1; i >= 0; i--) {
      const cssRule = styleSheet.cssRules[i];
      if (cssRule.name === "scrollSmooth") {
        styleSheet.deleteRule(i);
      }
    }

    styleSheet.insertRule(`
      @keyframes scrollSmooth {
        0% { transform: translateY(0); }
        100% { transform: translateY(-${scrollDistance}px); }
      }
    `, styleSheet.cssRules.length);

    Object.assign(listElement.style, {
      animationName: "scrollSmooth",
      animationDuration: animationDurationSeconds + "s",
      animationTimingFunction: "linear",
      animationIterationCount: "infinite",
      willChange: "transform"
    });
  }
}

customElements.define("search-move", SearchMove);

/* Video play at a time */
document.addEventListener('DOMContentLoaded', function () {
  const gallery = document.querySelector('[id^="MediaGallery"]');
  if (!gallery) return;
  const pauseAllVideos = (exceptVideo = null) => {
    const allVideos = gallery.querySelectorAll('video');
    allVideos.forEach((video) => {
      if (video !== exceptVideo && !video.paused) {
        video.pause();
      }
    });
  };
  gallery.addEventListener('click', function (event) {
    const clickedMedia = event.target.closest('[data-media-id]');
    if (!clickedMedia) return;
    const clickedVideo = clickedMedia.querySelector('video');
    pauseAllVideos(clickedVideo);
  });
  const observer = new MutationObserver(() => {
    const allVideos = gallery.querySelectorAll('video');
    allVideos.forEach((video) => {
      video.addEventListener('play', () => {
        pauseAllVideos(video);
      });
    });
  });
  observer.observe(gallery, {
    childList: true,
    subtree: true,
  });
});


// Cart bubble
document.addEventListener('DOMContentLoaded', function() {
  var cartCount = 0;
  var cartBubble = document.querySelector('.animate-btm-bubble');
  if (cartBubble) {
    var countElement = cartBubble.querySelector('[aria-hidden="true"]');
    if (countElement) {
      cartCount = parseInt(countElement.textContent, 10) || 0;
    }
  }
  
  function checkCartCount() {
    var bubble = document.querySelector('.animate-btm-bubble');
    if (!bubble) return;
    
    var countElement = bubble.querySelector('[aria-hidden="true"]');
    if (!countElement) return;
    
    var newCount = parseInt(countElement.textContent, 10) || 0;
    
    if (newCount !== cartCount) {
      cartCount = newCount;
      
      bubble.classList.add('animating');
      setTimeout(function() {
        bubble.classList.remove('animating');
      }, 600);
    }
  }
  
  setInterval(checkCartCount, 500);
  
  var originalFetch = window.fetch;
  window.fetch = function() {
    return originalFetch.apply(this, arguments).then(function(response) {
      
      if (response.url.includes('/cart') || response.url.includes('cart.js')) {
        setTimeout(checkCartCount, 300);
      }
      return response;
    });
  };
});

// video pause js
document.addEventListener('DOMContentLoaded', function () {
  const gallery = document.querySelector('[id^="MediaGallery"]');
  if (!gallery) return;
  const youtubePlayers = new Map();
  let youtubeAPILoaded = false;
  function loadYouTubeAPI() {
    if (youtubeAPILoaded) return;
    youtubeAPILoaded = true;
    const tag = document.createElement('script');
    tag.src = "https://www.youtube.com/iframe_api";
    const firstScriptTag = document.getElementsByTagName('script')[0];
    firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
  }
  function pauseAllVideos(exceptVideo = null) {
    const allVideos = gallery.querySelectorAll('video');
    allVideos.forEach((video) => {
      if (video !== exceptVideo && !video.paused) {
        video.pause();
      }
    });
    youtubePlayers.forEach((player, iframe) => {
      if (iframe !== exceptVideo) {
        player.pauseVideo();
      }
    });
    document.querySelectorAll('product-model').forEach((model) => {
      if (model.modelViewerUI) model.modelViewerUI.pause();
    });
  }
  gallery.addEventListener('click', function (event) {
    const clickedMedia = event.target.closest('[data-media-id]');
    if (!clickedMedia) return;
    const clickedVideo = clickedMedia.querySelector('video');
    if (clickedVideo) {
      pauseAllVideos(clickedVideo);
    }
    const clickedIframe = clickedMedia.querySelector('iframe');
    if (clickedIframe && clickedIframe.src.includes('youtube.com')) {
      pauseAllVideos(clickedIframe);
    }
  });
  const observer = new MutationObserver(() => {
    const allVideos = gallery.querySelectorAll('video');
    allVideos.forEach((video) => {
      video.addEventListener('play', () => {
        pauseAllVideos(video);
      });
    });
    const iframes = gallery.querySelectorAll('iframe[src*="youtube.com"]');
    if (iframes.length > 0) {
      loadYouTubeAPI();
      if (window.YT && window.YT.Player) {
        iframes.forEach((iframe) => {
          if (!youtubePlayers.has(iframe)) {
            const player = new YT.Player(iframe, {
              events: {
                onStateChange: function (event) {
                  if (event.data === YT.PlayerState.PLAYING) {
                    pauseAllVideos(iframe);
                  }
                }
              }
            });
            youtubePlayers.set(iframe, player);
          }
        });
      }
    }
  });
  observer.observe(gallery, {
    childList: true,
    subtree: true,
  });
  window.onYouTubeIframeAPIReady = function () {
    const iframes = gallery.querySelectorAll('iframe[src*="youtube.com"]');
    iframes.forEach((iframe) => {
      if (!youtubePlayers.has(iframe)) {
        const player = new YT.Player(iframe, {
          events: {
            onStateChange: function (event) {
              if (event.data === YT.PlayerState.PLAYING) {
                pauseAllVideos(iframe);
              }
            }
          }
        });
        youtubePlayers.set(iframe, player);
      }
    });
  };
});

document.addEventListener("DOMContentLoaded", () => {
  const quantityInputs = document.querySelectorAll("quantity-input, .quantity__input");
  quantityInputs.forEach(input => {
    input.addEventListener("focus", () => {
      input.scrollIntoView({
        behavior: "smooth",
        block: "center",
        inline: "nearest"
      });
    });
  });
});

function setFooterScrollBottom() {
  const footer = document.querySelector("#footer_scroll");
  if (!footer) return;
  const stickyBar = document.querySelector(".sticky_bottombar");
  const quickNotify = document.querySelector(".quick-notify.active");
  const stickyHeight = stickyBar ? stickyBar.offsetHeight : 0;
  const notifyHeight = quickNotify ? quickNotify.offsetHeight : 0;
  const totalBottom = stickyHeight + notifyHeight + 15;
  if (footer.style.bottom !== totalBottom + "px") footer.style.bottom = totalBottom + "px";
}
let footerScrollQueued = 0;
function queueFooterScrollBottom() {
  if (footerScrollQueued) return;
  footerScrollQueued = 1;
  const run = () => {
    footerScrollQueued = 0;
    setFooterScrollBottom();
  };
  if (window.requestIdleCallback) requestIdleCallback(run, { timeout: 500 });
  else setTimeout(run, 200);
}
document.addEventListener("DOMContentLoaded", queueFooterScrollBottom);
window.addEventListener("resize", queueFooterScrollBottom);
new MutationObserver(queueFooterScrollBottom).observe(document.body, {
  childList: true,
  subtree: true
});

// document.addEventListener('DOMContentLoaded', () => {
//   const footerScroll = document.querySelector('#footer_scroll');
//   const quickNotify = document.querySelector('.quick-notify');
//   const setDynamicHeights = () => {
//     let footerHeight = 0;
//     let quickNotifyHeight = 0;

//     if (footerScroll && !footerScroll.classList.contains('scrollbtn_hide')) {
//       footerHeight = footerScroll.offsetHeight;
//     }

//     if (quickNotify && quickNotify.classList.contains('active')) {
//       quickNotifyHeight = quickNotify.offsetHeight;
//     }

//     document.documentElement.style.setProperty(
//       '--footer-scroll-height',
//       `${footerHeight}px`
//     );

//     document.documentElement.style.setProperty(
//       '--quick-notify-height',
//       `${quickNotifyHeight}px`
//     );
//   };

//   setDynamicHeights();

//   window.addEventListener('resize', setDynamicHeights);

//   if (footerScroll) {
//     new MutationObserver(setDynamicHeights).observe(footerScroll, {
//       attributes: true,
//       childList: true,
//       subtree: true
//     });
//   }

//   if (quickNotify) {
//     new MutationObserver(setDynamicHeights).observe(quickNotify, {
//       attributes: true,
//       attributeFilter: ['class']
//     });
//   }
// });

document.addEventListener('DOMContentLoaded', () => {
  const footerScroll = document.querySelector('#footer_scroll');
  const quickNotify = document.querySelector('.quick-notify');
  const subscribeNow = document.querySelector('.subscribe_now');

  if (!subscribeNow) return;

  const updateOffset = () => {
    let offset = 0;

    if (footerScroll && !footerScroll.classList.contains('scrollbtn_hide')) {
      offset += footerScroll.offsetHeight;
    }

    if (quickNotify && quickNotify.classList.contains('active')) {
      offset += quickNotify.offsetHeight;
    }

    subscribeNow.style.transform = `translateY(calc(-${offset}px + 45px))`;
  };

  updateOffset();
  window.addEventListener('resize', updateOffset);

  if (footerScroll) {
    new MutationObserver(updateOffset).observe(footerScroll, {
      attributes: true,
      childList: true,
      subtree: true
    });
  }

  if (quickNotify) {
    new MutationObserver(updateOffset).observe(quickNotify, {
      attributes: true,
      attributeFilter: ['class']
    });
  }
});

// Video
document.querySelectorAll('.video-content-remove').forEach((close) => {
  close.addEventListener('click', (event) => {
    const parentElement = event.currentTarget.closest('.video_box');
    if (parentElement) {
      parentElement.remove();
    }
  });
});
/* ============================================================
   FAST ROUND ADD-TO-CART + CLEAR CART
   Mirrors product-form.js exactly — single fetch, direct renderContents
   ============================================================ */
(function () {

  document.addEventListener('click', function (e) {

    /* ── Round ATC button ── */
    var btn = e.target.closest('.card-round-atc-btn[data-variant-id]');
    if (btn) {
      var variantId = btn.dataset.variantId;
      if (!variantId) return;

      btn.classList.add('card-atc-loading');
      btn.disabled = true;

      var cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

      // Mirror fetchConfig('javascript') from global.js
      var config = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/javascript',
          'X-Requested-With': 'XMLHttpRequest'
        }
      };
      delete config.headers['Content-Type']; // FormData sets this automatically

      // Build FormData exactly like product-form.js
      var formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', '1');
      if (cart) {
        formData.append(
          'sections',
          cart.getSectionsToRender().map(function(s) { return s.id; })
        );
        formData.append('sections_url', window.location.pathname);
        cart.setActiveElement && cart.setActiveElement(document.activeElement);
      }
      config.body = formData;

      var preOpened = false;
      if (cart && cart.tagName === 'CART-DRAWER' && document.body.dataset.cartType !== 'sticky_cart_bar' && typeof cart.open === 'function') {
        cart.classList.add('hm-atc-pending');
        cart.open();
        preOpened = true;
      }

      fetch(routes.cart_add_url, config)
        .then(function(r) { return r.json(); })
        .then(function(response) {
          if (response.status) {
            console.error('ATC error:', response.description);
            if (preOpened) cart.close();
            return;
          }
          if (!cart) { window.location = routes.cart_url; return; }

          // Exact same call as product-form.js line 87
          if (document.body.dataset.cartType === 'sticky_cart_bar') {
            cart.renderContents(response, false);
            var qn = document.querySelector('.quick-notify');
            if (qn) qn.classList.add('active');
          } else {
            cart.classList.remove('hm-atc-pending');
            cart.renderContents(response, !preOpened);
            if (preOpened && typeof trapFocus === 'function') {
              var cdInner = document.getElementById('CartDrawer');
              if (cdInner) trapFocus(cdInner, cart.querySelector('.drawer__inner'));
            }
          }

          if (cart.classList.contains('is-empty')) cart.classList.remove('is-empty');

          if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'product-form',
              productVariantId: variantId,
              cartData: response
            });
          }
        })
        .catch(function(e) {
          console.error(e);
          if (preOpened) cart.close();
        })
        .finally(function() {
          if (cart) cart.classList.remove('hm-atc-pending');
          btn.classList.remove('card-atc-loading');
          btn.disabled = false;
        });
      return;
    }

    /* ── Clear Cart button ── */
    var clearBtn = e.target.closest('#CartDrawer-ClearCart');
    if (clearBtn) {
      clearBtn.disabled = true;
      var origHTML = clearBtn.innerHTML;
      clearBtn.textContent = 'Clearing...';

      var cart = document.querySelector('cart-drawer');

      fetch('/cart/clear.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
      })
      .then(function(r) { return r.json(); })
      .then(function() {
        if (!cart) { window.location.reload(); return; }

        var sectionIds = cart.getSectionsToRender().map(function(s) { return s.id; });

        return fetch('/?sections=' + sectionIds.join(','), {
          headers: { 'X-Requested-With': 'XMLHttpRequest' }
        })
        .then(function(r) { return r.json(); })
        .then(function(sections) {
          // Update all section HTML
          cart.getSectionsToRender().forEach(function(section) {
            var el = section.selector
              ? document.querySelector(section.selector)
              : document.getElementById(section.id);
            if (!el || !sections[section.id]) return;
            var parser = new DOMParser();
            var doc = parser.parseFromString(sections[section.id], 'text/html');
            var inner = doc.querySelector(section.selector || '.shopify-section');
            if (inner) el.innerHTML = inner.innerHTML;
          });

          // Set is-empty state on cart-drawer and inner div
          cart.classList.add('is-empty');
          var drawerInner = cart.querySelector('.drawer__inner');
          if (drawerInner) drawerInner.classList.add('is-empty');

          // Open drawer to show empty state
          cart.open && cart.open();

          // Update cart icon bubble
          var bubble = document.getElementById('cart-icon-bubble');
          if (bubble && sections['cart-icon-bubble']) {
            var parser2 = new DOMParser();
            var doc2 = parser2.parseFromString(sections['cart-icon-bubble'], 'text/html');
            var newBubble = doc2.querySelector('.shopify-section');
            if (newBubble) bubble.innerHTML = newBubble.innerHTML;
          }

          if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'clear-cart' });
          }
        });
      })
      .catch(function(err) { console.error('Clear cart error:', err); })
      .finally(function() {
        clearBtn.disabled = false;
        clearBtn.innerHTML = origHTML;
      });
    }

  });
})();

// Sticky Header
class StickyHeader extends HTMLElement {
  connectedCallback() {
    this.stickyType = this.dataset.stickyType || 'on-scroll-up';
    this.section = this.closest('.section-header');
    this.headerMain = this.querySelector('.header_main');
    this.lastScrollTop = 0;

    if (!this.section) return;

    // Add sticky class to both section and header_main
    // CSS handles desktop (section sticky) vs mobile (header_main sticky)
    this.section.classList.add('shopify-section-header-sticky', 'animate');
    if (this.headerMain) this.headerMain.classList.add('shopify-section-header-sticky');

    if (this.stickyType === 'on-scroll-up' || this.stickyType === 'reduce-logo-size') {
      this._onScroll = this.onScroll.bind(this);
      window.addEventListener('scroll', this._onScroll, { passive: true });
    }
  }

  onScroll() {
    if (document.body.style.position === 'fixed') return;
    const scrollTop = window.scrollY;
    const headerHeight = this.section.offsetHeight || 80;

    if (this.stickyType === 'reduce-logo-size') {
      if (scrollTop > headerHeight) {
        document.body.classList.add('scrolled-past-header');
      } else {
        document.body.classList.remove('scrolled-past-header');
      }
    }

    if (this.stickyType === 'on-scroll-up' || this.stickyType === 'reduce-logo-size') {
      if (scrollTop > this.lastScrollTop && scrollTop > headerHeight) {
        // Scrolling down — hide
        this.section.classList.add('shopify-section-header-hidden');
      } else {
        // Scrolling up — show
        this.section.classList.remove('shopify-section-header-hidden');
      }
    }

    this.lastScrollTop = scrollTop <= 0 ? 0 : scrollTop;
  }

  disconnectedCallback() {
    if (this._onScroll) window.removeEventListener('scroll', this._onScroll);
    document.body.classList.remove('scrolled-past-header');
  }
}

if (!customElements.get('sticky-header')) {
  customElements.define('sticky-header', StickyHeader);
}
(function () {
  var open = false;
  document.addEventListener('toggle', function (e) {
    var d = e.target;
    if (d && d.matches && d.matches('[data-cd-extras]')) open = d.open;
  }, true);
  function sync() {
    if (!open) return;
    document.querySelectorAll('[data-cd-extras]').forEach(function (d) {
      if (!d.open) d.open = true;
    });
  }
  function watch() {
    var cd = document.querySelector('cart-drawer');
    if (!cd || cd.__cdExtrasWatch) return;
    cd.__cdExtrasWatch = true;
    new MutationObserver(sync).observe(cd, { childList: true, subtree: true });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', watch);
  else watch();
})();

document.addEventListener('click', function (e) {
  var ov = e.target && e.target.closest && e.target.closest('#CartDrawer-Overlay');
  if (!ov) return;
  var drawer = ov.closest('cart-drawer');
  if (drawer && drawer.classList.contains('active') && typeof drawer.close === 'function') drawer.close();
});
